<template>
  <!-- DISABLED IS NOT ABSENT. A channel whose curve the payload does not carry yet has nothing for
       these three to move, and the wizard that draws them draws them anyway, greyed: a control that
       arrives on the screen the moment two figures are typed is a control the user meets for the
       first time exactly when they were not looking for it. Shown from the start, it is a promise of
       what answering the figures buys, and its arrival is a colour rather than a component. -->
  <div class="curve" :class="{ 'curve--disabled': disabled }">
    <div v-for="lever in levers" :key="lever.key" class="curve__lever">
      <div class="d-flex align-center">
        <span class="caption grey--text text--darken-2 text-truncate">{{ lever.label }}</span>
        <v-tooltip top max-width="380">
          <template v-slot:activator="{ on }">
            <v-icon x-small class="ml-1" v-on="on">mdi-information-outline</v-icon>
          </template>
          <span><template v-if="lever.technical"><b>{{ lever.technical }}</b> — </template>{{ lever.info }}</span>
        </v-tooltip>
        <!-- What this lever stands at against what the model itself said. The one figure that says
             "this channel is no longer a copy", and it is per lever because the three are moved
             one at a time. -->
        <span v-if="lever.factor" class="curve__factor">{{ lever.factor }}</span>
        <v-spacer></v-spacer>
        <v-text-field
          :value="lever.field"
          type="number"
          step="any"
          dense
          hide-details
          single-line
          :disabled="disabled"
          class="pa-0 ma-0 curve__value"
          @change="onField(lever.key, $event)"
        ></v-text-field>
      </div>

      <!-- The slider's own range stays the one hillCurve gives it, so the thumb goes on tracking the
           number in the field beside it. What is held inside the corridor is the VALUE: a drag past
           a rule lands on the rule, and the ground past it is greyed on the track with the reason on
           it, rather than the user discovering the wall by pushing against it. -->
      <div class="curve__track">
        <!-- Inset by the thumb's own radius, which is where Vuetify's track starts and ends: the
             spans below are placed as percentages of the range, and a percentage of the wrapper
             would put both walls a thumb's width off. -->
        <span class="curve__ground">
          <span
            v-for="(blocked, index) in lever.blocked"
            :key="`blocked_${lever.key}_${index}`"
            class="curve__blocked"
            :title="blocked.title"
            :style="blocked.style"></span>
        </span>
        <v-slider
          :value="lever.value"
          :min="lever.min"
          :max="lever.max"
          :step="lever.step"
          dense
          hide-details
          :disabled="disabled"
          class="mt-0 pt-0"
          @input="onSlide(lever.key, $event)"
          @change="onCommit(lever.key, $event)"
        ></v-slider>
      </div>
    </div>

    <!-- The note, and the way back to the imported fit. The way back is not always DRAWN here: a
         wizard that has a bar of its own along the bottom of the screen can take it — undoing an
         edit is a statement about the whole channel and not about these three sliders, and the
         zone bar's grips move the same parameters — in which case it says so and this foot carries
         the note alone. The button is the same button either way: one `reset` event, one condition
         for whether there is anything to go back to. -->
    <div v-if="note || !hideReset" class="curve__foot">
      <span class="curve__note">{{ note }}</span>
      <v-spacer></v-spacer>
      <v-btn v-if="!hideReset" text x-small :disabled="disabled || !canReset" @click="$emit('reset')">
        <v-icon x-small class="mr-1">mdi-restore</v-icon>{{ labels.reset }}
      </v-btn>
    </div>
  </div>
</template>

<script>
// The three Hill levers of one channel, and nothing else.
//
// It owns no arithmetic about the payload: every range arrives through `bounds`, every wall through
// `limits`, and every reading the wizard shows beside it is computed in the wizard off ekrmClone.
// That is not tidiness — a component cannot be mounted in a test in this project (vue and
// vue-template-compiler are a patch apart, so @vue/test-utils refuses to load), so anything that can
// be pinned by a test has to live somewhere else. What is left here is geometry and a pointer.
//
// The slider is Vuetify's rather than hand-rolled HTML, which is where this parts company with the
// seasonality editor: there the twelve bars ARE the plot and a pointer had to map onto them, here
// the plot is a curve drawn beside the levers and a lever is a lever.
import { sliderValue } from '@/utils/sliderValue.js'

const KEYS = ['vmax', 'k', 'n']

export default {
  name: 'SaturationLevers',
  props: {
    // Where the three levers stand now, as the built form carries them.
    params: { type: Object, required: true },
    // The fit this channel was imported with. The reset goes back to it and every lever's factor is
    // measured against it.
    imported: { type: Object, default: null },
    // { vmax: { min, max, step }, k: …, n: … } — cloneCurveBounds, which is hillCurve's sliderBounds.
    bounds: { type: Object, required: true },
    // { vmax: { min: { value, reason }, max: { value, reason } }, … }, or null while there is none.
    // Read at the values the form carries, so it is right for as long as the other two levers stand
    // still — which is the whole of a gesture.
    limits: { type: Object, default: null },
    // (reason) => string, for the tooltip on the ground a lever may not enter.
    reasonLabel: { type: Function, required: true },
    // Whether the way back to the imported fit is drawn in this component's own foot. Off by
    // default: every caller that has not said otherwise gets the button where it has always been.
    hideReset: { type: Boolean, default: false },
    // (key, value) => string, for the number beside each lever. Vmax is a revenue and K a spend;
    // only the wizard knows what either is denominated in.
    formatValue: { type: Function, required: true },
    labels: { type: Object, required: true },
    // What the fit was worth, said once where the change is made.
    note: { type: String, default: '' },
    // Whether these three may be moved at all. Off for a channel the payload does not carry yet:
    // there is a shape to show — the one it was imported with, placed at whatever the two figures
    // beside it currently say — and nothing to commit a move to.
    disabled: { type: Boolean, default: false },
    // Whether a moved lever wears what it stands at against the imported fit. A caller whose screen
    // does not ask the reader to hold onto where a shape was imported from switches it off; the
    // reset still goes back to that fit either way.
    showFactor: { type: Boolean, default: true }
  },
  data () {
    return {
      // The values under the pointer. Committed values arrive through `params`; this is what a drag
      // moves before it is let go, so the slider does not fight the form it has not written to yet.
      draft: null
    }
  },
  computed: {
    current () {
      return this.draft || this.params
    },
    levers () {
      return KEYS.map(key => {
        const range = this.bounds[key] || { min: 0, max: 1, step: 0.01 }
        const value = Number(this.current[key])
        return {
          key: key,
          label: this.labels[key],
          info: this.labels[`${key}Info`],
          technical: this.labels[`${key}Technical`],
          min: range.min,
          max: range.max,
          step: range.step,
          // ON THE SLIDER'S OWN GRID, and the field beside it not. A slider handed a number it
          // would have to clamp or round EMITS the correction as though the thumb had been moved —
          // see sliderValue — so a curve moved from somewhere else comes back through here as an
          // edit nobody made. The number itself is the field's to show, and the field has no grid.
          value: sliderValue(value, range),
          field: this.formatValue(key, value),
          factor: this.showFactor ? this.factorLabel(key) : '',
          blocked: this.blockedFor(key, range)
        }
      })
    },
    canReset () {
      if (!this.imported) return false
      return KEYS.some(key => this.moved(key))
    }
  },
  watch: {
    // A rebuild lands here as new `params`; the draft has served its purpose by then and holding on
    // to it would leave the levers showing a plan the form no longer carries.
    params () {
      this.draft = null
    }
  },
  methods: {
    moved (key) {
      if (!this.imported) return false
      const from = Number(this.imported[key])
      const to = Number(this.params[key])
      if (!isFinite(from) || !isFinite(to) || from === 0) return false
      return Math.abs(to / from - 1) > 0.005
    },
    factorLabel (key) {
      if (!this.moved(key)) return ''
      return `×${Math.round(Number(this.params[key]) / Number(this.imported[key]) * 100) / 100}`
    },
    // The two spans of the track this lever may not enter, as percentages of the range the slider is
    // drawn across. Empty where nothing binds, which for the knee and the exponent is the usual
    // answer.
    blockedFor (key, range) {
      const limit = (this.limits || {})[key]
      const span = Number(range.max) - Number(range.min)
      if (!limit || !(span > 0)) return []
      const at = value => 100 * (Number(value) - Number(range.min)) / span
      const blocked = []
      const add = (from, to, reason) => {
        const low = Math.max(0, Math.min(100, from))
        const high = Math.max(0, Math.min(100, to))
        if (high - low <= 0.01 || !reason) return
        blocked.push({
          title: this.reasonLabel(reason),
          style: { left: `${low}%`, width: `${high - low}%` }
        })
      }
      add(0, at(limit.min.value), limit.min.reason)
      add(at(limit.max.value), 100, limit.max.reason)
      return blocked
    },
    clamp (key, value) {
      const number = Number(value)
      if (!isFinite(number) || number <= 0) return null
      const limit = (this.limits || {})[key]
      if (!limit) return number
      const low = Number(limit.min.value)
      const high = Number(limit.max.value)
      let held = number
      if (isFinite(low) && held < low) held = low
      if (isFinite(high) && held > high) held = high
      return held
    },
    withKey (key, value) {
      const next = { vmax: this.current.vmax, k: this.current.k, n: this.current.n }
      next[key] = value
      return next
    },
    onSlide (key, value) {
      const held = this.clamp(key, value)
      if (held === null) return
      this.draft = this.withKey(key, held)
      this.$emit('preview', this.draft)
    },
    onCommit (key, value) {
      const held = this.clamp(key, value)
      if (held === null) {
        this.draft = null
        return
      }
      this.$emit('input', this.withKey(key, held))
    },
    // A number typed into the field is the same commit as letting the slider go at it, and is held
    // to the same walls: this is a lever either way, not a box the user is entitled to overrun the
    // way the two headline figures are.
    onField (key, value) {
      const held = this.clamp(key, value)
      if (held === null) {
        this.draft = null
        return
      }
      this.$emit('input', this.withKey(key, held))
    }
  }
}
</script>

<style scoped>
  /* Greyed rather than gone. Vuetify greys the slider and the field on its own through `disabled`;
     what is left is the type around them — the labels, the factors and the note — which is ours. */
  .curve--disabled {
    opacity: 0.55;
  }

.curve__lever {
  margin-bottom: 4px;
}

.curve__factor {
  margin-left: 6px;
  font-size: 11px;
  color: #7E57C2;
  white-space: nowrap;
}

/* Wide enough for a seven-figure ceiling, right aligned so the three of them line up, and stripped
   of the spinner arrows, which at this size are bigger than the digits. The same field the expert
   wizard puts beside its own levers. */
.curve__value {
  max-width: 110px;
  font-size: 0.8rem;
}

.curve__value >>> input {
  text-align: right;
  padding: 0;
}

.curve__value >>> input[type="number"] {
  -moz-appearance: textfield;
}

.curve__value >>> input[type="number"]::-webkit-outer-spin-button,
.curve__value >>> input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* The slider and the ground it may not enter share one box, so a span placed by percentage lands on
   the track it is describing. */
.curve__track {
  position: relative;
}

.curve__ground {
  position: absolute;
  left: 6px;
  right: 6px;
  top: 0;
  bottom: 0;
  /* So a drag keeps every frame: the ground is a statement, not a target. */
  pointer-events: none;
}

.curve__blocked {
  position: absolute;
  top: 50%;
  height: 8px;
  transform: translateY(-50%);
  background: repeating-linear-gradient(
    45deg,
    rgba(120, 120, 120, 0.18),
    rgba(120, 120, 120, 0.18) 3px,
    rgba(120, 120, 120, 0.06) 3px,
    rgba(120, 120, 120, 0.06) 6px
  );
  border-radius: 2px;
  z-index: 1;
}

.curve__foot {
  display: flex;
  align-items: center;
  margin-top: 8px;
}

.curve__note {
  font-size: 11px;
  line-height: 1.35;
  color: rgba(0, 0, 0, 0.6);
}
</style>
